import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment as env } from '../../../environments/environment';
import { AuthenticationFacade } from './authentication.facade';
import { getUserSub, parseJwt } from './jwt-helpers';

interface SelectOrganizationResponse {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  orgId?: string;
  organization_ids?: string[];
}

/**
 * Cambia la organización activa del usuario re-emitiendo un token scoped a la org destino
 * (con los roles que el usuario tiene en ella) vía la auth API. Aplica la nueva sesión y
 * notifica al resto de la app con `mb:sessionEstablished` para que se recalculen org, roles
 * y servicios disponibles.
 */
@Injectable({
  providedIn: 'root',
})
export class OrganizationSwitchService {
  /** HttpClient con backend directo para no pasar por el AuthInterceptor (fijamos el bearer a mano). */
  private readonly http: HttpClient;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private auth: AuthenticationFacade,
    httpBackend: HttpBackend
  ) {
    this.http = new HttpClient(httpBackend);
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private authBase(): string {
    return String(env.api.endpoints.auth.endpoint || '').replace(/\/+$/, '');
  }

  /**
   * Selecciona una nueva organización activa. Re-emite la sesión scoped a esa org.
   * Lanza si no hay sesión o si el backend rechaza (p. ej. el usuario no pertenece a la org).
   */
  async switchOrganization(organizationId: string): Promise<void> {
    if (!this.isBrowser()) return;

    const orgId = String(organizationId || '').trim();
    if (!orgId) {
      throw new Error('organizationId es requerido');
    }

    const token = this.auth.getAccessToken();
    if (!token) {
      throw new Error('No hay una sesión activa');
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const response = await firstValueFrom(
      this.http.post<SelectOrganizationResponse>(
        `${this.authBase()}/auth/select-organization`,
        { organization_id: orgId },
        { headers }
      )
    );

    if (!response?.accessToken) {
      throw new Error('Respuesta inválida del servidor al cambiar de organización');
    }

    this.applySession(response, orgId);
  }

  /** Persiste los nuevos tokens, actualiza el usuario en sesión y notifica a la app. */
  private applySession(response: SelectOrganizationResponse, requestedOrgId: string): void {
    const accessToken = response.accessToken;
    const idToken = response.idToken || accessToken;
    const refreshToken = response.refreshToken || '';
    const orgId = response.orgId || requestedOrgId;

    try {
      sessionStorage.setItem('mb.accessToken', accessToken);
      sessionStorage.setItem('mb.idToken', idToken);
      sessionStorage.setItem('mb.refreshToken', refreshToken);
      const expiresAt = Math.floor(Date.now() / 1000) + (response.expiresIn || 8 * 60 * 60);
      sessionStorage.setItem('mb.expiresAt', String(expiresAt));
      if (orgId) {
        sessionStorage.setItem('mb.currentOrgId', orgId);
      }
    } catch {}

    let userId = '';
    let roles: string[] = [];
    try {
      userId = getUserSub(idToken);
      const payload = parseJwt(accessToken) as Record<string, unknown>;
      roles = Array.isArray(payload?.['roles']) ? (payload['roles'] as string[]) : [];
    } catch {}

    this.auth.setCurrentUser({
      userId,
      orgId,
      accessToken,
      idToken,
      refreshToken,
      roles,
    });

    // Notificar el cambio de sesión: OrganizationContextService re-resuelve la org y refresca
    // los servicios disponibles; SessionContext/Authorization recalculan roles del nuevo token.
    try {
      window.dispatchEvent(new CustomEvent('mb:sessionEstablished'));
    } catch {}
  }
}
